import React, { useEffect, useMemo, useState } from "react"

import { Empty, Flex, Spin, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Private from "../../../../Components/Private"
import SelectComponent from "../../../../Components/Select"
import TableComponent from "../../../../Components/Table"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { byCardTypeEmptyData } from "../../utils/emptyData"
import HealthBanner from "../HealthBanner"
import { formatAmount, formatCount } from "../Table/columns"
import { createTableSummary } from "../Table/tableSummary"
import { getColumns, getRowIcon, getRowName } from "./columns"

import "./styles.less"

const { Title, Text } = Typography

const ByCardTypeBlock = () => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const dataByCardTypeFetching = useSelector((state) => state.health.dataByCardTypeFetching)
  const dataByCardType = useSelector((state) => state.health.dataByCardType)
  const { is_business_health_available } = useSelector((state) => state.health.mainInfo)

  const { result, summary, other, comparison_period, comparison_summary } =
    is_business_health_available ? dataByCardType : byCardTypeEmptyData
  const comparisonLabel = comparison_period?.label

  const [expandedKeys, setExpandedKeys] = useState([])
  const [mobileMetric, setMobileMetric] = useState("transactions")

  useEffect(() => {
    setExpandedKeys([])
  }, [result])

  const dataSource = useMemo(() => {
    if (!result) return []

    const mainRows = result?.reduce((acc, item) => {
      const cardTypeRow = {
        ...item,
        unique_id: item?.card_type
      }
      if (expandedKeys.includes(cardTypeRow.card_type)) {
        const wallets = (cardTypeRow?.wallets || []).map((wallet) => ({
          ...wallet,
          row_type: "wallet",
          card_type: cardTypeRow?.card_type,
          unique_id: `${cardTypeRow?.card_type}-${wallet?.wallet_type}`
        }))
        return [...acc, cardTypeRow, ...wallets]
      }
      return [...acc, cardTypeRow]
    }, [])

    const otherRows = (other?.wallets ?? []).map((wallet) => ({
      ...wallet,
      row_type: "wallet",
      card_type: "other",
      unique_id: `other-${wallet?.wallet_type}`
    }))

    const otherDivider = {
      unique_id: "other_divider",
      isDivider: true
    }
    if (!otherRows?.length) {
      return mainRows
    }
    return [...mainRows, otherDivider, ...otherRows]
  }, [result, expandedKeys])

  const columnsByCardType = getColumns({ t, expandedKeys })

  const tableSettings = useMemo(
    () => ({
      scroll: dataSource?.length > 0 ? { x: "auto", y: isMobile ? 350 : undefined } : {}
    }),
    [dataSource, isMobile]
  )

  const tableSummary = useMemo(() => createTableSummary(t), [t])

  const mobileMetricOptions = useMemo(
    () => [
      { label: t("Transactions"), value: "transactions" },
      { label: t("Chargebacks"), value: "chargebacks" },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Chargeback rate"), value: "chargeback_rate" },
      { label: t("Prevention rate"), value: "prevention_rate" }
    ],
    [t]
  )

  const selectedMobileMetricLabel =
    mobileMetricOptions.find((item) => item.value === mobileMetric)?.label || t("Metric")
  const hasTableData = dataSource.length > 0

  const rowClickHandler = (record) => {
    if (record.row_type === "wallet") return
    if (!record?.wallets?.length) return
    const key = record.card_type
    setExpandedKeys((prevKeys) =>
      prevKeys.includes(key) ? prevKeys.filter((k) => k !== key) : [...prevKeys, key]
    )
  }

  const renderMobileMetricCell = (record) => {
    if (record?.isDivider) return null

    switch (mobileMetric) {
      case "transactions":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.transactions_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.transactions_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargeback_rate":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{record?.chargeback_rate}%</Text>
            <Text type="secondary" strong>
              {record?.chargeback_rate_without_us}% {t("without us")}
            </Text>
          </Flex>
        )
      case "prevention_rate":
        return <Text strong>{record?.prevention_rate}%</Text>
      default:
        return null
    }
  }

  return (
    <Flex gap={24} vertical={true} className="card-block-with-table">
      <Spin spinning={dataByCardTypeFetching}>
        <Title level={5} style={{ marginBottom: 16, padding: "0 16px", fontWeight: 600 }}>
          {t("Breakdown by Payment Method")}
        </Title>

        {isMobile ? (
          <Flex vertical={true} gap={12} className="by-card-type-mobile-table">
            <div className="by-card-type-mobile-table__select-wrap">
              <SelectComponent
                size="large"
                value={mobileMetric}
                onChange={setMobileMetric}
                options={mobileMetricOptions}
                style={{ width: "100%" }}
              />
            </div>

            <div className="by-card-type-mobile-table__header">
              <Text type="secondary">{t("Card type")}</Text>
              <Text type="secondary" className="by-card-type-mobile-table__header-right">
                {selectedMobileMetricLabel}
              </Text>
            </div>

            {hasTableData ? (
              dataSource.map((record, index) => {
                if (record?.isDivider) {
                  return (
                    <div key={record.unique_id} className="by-card-type-mobile-table__divider">
                      <Text type="secondary">OTHER</Text>
                    </div>
                  )
                }

                const isWalletRow = record?.row_type === "wallet"
                const isExpandable = record?.wallets?.length > 0 && !isWalletRow
                const isExpanded = expandedKeys.includes(record?.card_type)
                const isBeforeDivider = dataSource[index + 1]?.isDivider
                const isLastRow = index === dataSource.length - 1
                const image = getRowIcon(record)
                const text = getRowName(record)

                return (
                  <div
                    key={record.unique_id}
                    className={cls("by-card-type-mobile-table__row", {
                      "by-card-type-mobile-table__row--expandable": isExpandable,
                      "by-card-type-mobile-table__row--wallet": isWalletRow,
                      "by-card-type-mobile-table__row--before-divider": isBeforeDivider,
                      "by-card-type-mobile-table__row--last": isLastRow
                    })}
                    onClick={() => rowClickHandler(record)}
                  >
                    <Flex gap={8} align="center" className="by-card-type-mobile-table__left">
                      {image && <img src={image} alt={text} width={20} height={20} />}
                      <Text strong>{text}</Text>
                      {isExpandable && (
                        <Icon
                          type="chevron_down_small"
                          className={cls("chevron_down", {
                            expanded: isExpanded
                          })}
                        />
                      )}
                    </Flex>
                    <div className="by-card-type-mobile-table__right">
                      {renderMobileMetricCell(record)}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="by-card-type-mobile-table__empty">
                <Empty
                  image={Images.no_data}
                  imageStyle={{ width: 64, height: 40 }}
                  description={t("No Data")}
                />
              </div>
            )}

            {hasTableData && summary && (
              <div className="by-card-type-mobile-table__row by-card-type-mobile-table__row--summary">
                <Text type="secondary" strong>
                  {t("Summary")}
                </Text>
                <div className="by-card-type-mobile-table__right">
                  {renderMobileMetricCell(summary)}
                </div>
              </div>
            )}

            {hasTableData && comparison_summary && (
              <div className="by-card-type-mobile-table__row by-card-type-mobile-table__row--compare">
                <Text type="secondary" strong>
                  {t("Compare")} {comparisonLabel || t("previous period")}
                </Text>
                <div className="by-card-type-mobile-table__right">
                  {renderMobileMetricCell(comparison_summary)}
                </div>
              </div>
            )}
          </Flex>
        ) : (
          <TableComponent
            className="by-card-type-table"
            columns={columnsByCardType}
            rowKey={"unique_id"}
            loading={dataByCardTypeFetching}
            dataSource={dataSource}
            scroll={tableSettings.scroll}
            showSizeChanger={false}
            withOutPagination={true}
            onRow={(record) => {
              const isExpandable = record?.wallets?.length > 0
              return {
                onClick: () => rowClickHandler(record),
                className: cls({
                  "expandable-row": isExpandable,
                  divider: record?.isDivider
                })
              }
            }}
            summary={() =>
              tableSummary(summary, dataSource, undefined, comparison_summary, comparisonLabel)
            }
            pagination={false}
          />
        )}
      </Spin>
      <HealthBanner />
    </Flex>
  )
}

export default React.memo(ByCardTypeBlock)
